import React from 'react';

import { useClients } from '@/clients/queries';
import { ListboxOption } from '@/common/components/listbox';
import type { useCreateUpdateContactForm } from '@/contacts/hooks/use-create-update-contact-form';
import { useStateLookups } from '@/shared/lookups/queries';

type CreateUpdateContactFormProps = {
  form: ReturnType<typeof useCreateUpdateContactForm>;
  formId: string;
};

export function CreateUpdateContactForm({ form, formId }: CreateUpdateContactFormProps) {
  const clientsQuery = useClients();
  const clientLookups = clientsQuery.data?.map(client => ({
    id: client.id,
    name: client.name
  })) ?? [];
  const stateLookupsQuery = useStateLookups({});
  const stateLookups = stateLookupsQuery?.data ?? [];

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();

    void form.handleSubmit();
  }

  return (
    <form id={formId} onSubmit={submit} className="gap-6 flex flex-col">
      {/* Client Selection */}
      <form.AppField
        name="client"
        children={field => (
          <field.FormListbox label="Client" placeholder="Select a client" clearable>
            {clientLookups.length === 0 ?
                (
                  <ListboxOption value="" disabled>
                    No clients available
                  </ListboxOption>
                ) :
                (
                  clientLookups.map(client => (
                    <ListboxOption key={client.id} value={client}>
                      {client.name}
                    </ListboxOption>
                  ))
                )}
          </field.FormListbox>
        )}
      />

      {/* Name Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.AppField
          name="firstName"
          children={field => <field.FormInput label="First Name *" />}
        />
        <form.AppField
          name="lastName"
          children={field => <field.FormInput label="Last Name *" />}
        />
      </div>

      {/* Contact Information */}
      <form.AppField
        name="title"
        children={field => <field.FormInput label="Title" />}
      />

      <form.AppField
        name="email"
        children={field => <field.FormInput label="Email" type="email" />}
      />

      {/* Phone Numbers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.AppField
          name="workPhone"
          children={field => <field.FormInput label="Work Phone" type="tel" />}
        />
        <form.AppField
          name="mobilePhone"
          children={field => <field.FormInput label="Mobile Phone" type="tel" />}
        />
      </div>

      {/* Address */}
      <div className="space-y-4">
        <form.AppField
          name="addressLine1"
          children={field => <field.FormInput label="Address Line 1" />}
        />
        <form.AppField
          name="addressLine2"
          children={field => <field.FormInput label="Address Line 2" />}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <form.AppField
            name="city"
            children={field => <field.FormInput label="City" />}
          />
          <form.AppField
            name="state"
            children={field => (
              <field.FormListbox label="State" placeholder="Select state" clearable>
                {stateLookups.length === 0 ?
                    (
                      <ListboxOption value="" disabled>
                        No states available
                      </ListboxOption>
                    ) :
                    (
                      stateLookups.map(state => (
                        <ListboxOption key={state.code} value={state.code}>
                          {state.name}
                        </ListboxOption>
                      ))
                    )}
              </field.FormListbox>
            )}
          />
          <form.AppField
            name="zipCode"
            children={field => <field.FormInput label="Zip Code" />}
          />
        </div>
      </div>
    </form>
  );
}
